import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Cable, Cpu, Radio, ShieldCheck, Activity } from "lucide-react";

const STATS = [
  { value: "500+", label: "Installations" },
  { value: "98%", label: "Network Uptime" },
  { value: "24/7", label: "On-call Support" },
];

const PILLS = [
  { Icon: Cable, label: "Fiber Optic" },
  { Icon: ShieldCheck, label: "CCTV Security" },
  { Icon: Radio, label: "Wi-Fi Heatmap" },
  { Icon: Cpu, label: "Hardware" },
  { Icon: Activity, label: "Performance" },
];

const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-28 pb-24 md:pt-36 md:pb-32 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-grid pointer-events-none" />
      <div className="orb bg-[#FF6B00]/40 w-[520px] h-[520px] -top-40 -right-40" />
      <div className="orb bg-[#0B1320] w-[600px] h-[600px] -bottom-60 -left-40 opacity-90" />
      <div className="noise" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left content */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-full text-xs tracking-[0.18em] uppercase text-[#A0B2C6] mb-7"
            data-testid="hero-badge"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF6B00] pulse-dot" />
            Networking · Application Engineering Partner
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="hero-title"
            className="font-display font-bold text-[44px] sm:text-[64px] lg:text-[78px] leading-[0.95] tracking-tighter"
          >
            We don't just <span className="text-[#A0B2C6]">supply.</span>
            <br />
            We deliver <span className="text-[#FF6B00]">solutions.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            data-testid="hero-subtitle"
            className="mt-7 max-w-xl text-lg text-[#A0B2C6] leading-relaxed"
          >
            End-to-end network infrastructure, fiber optics, CCTV, biometric access, and
            electrical solutions — engineered, tested, and certified for businesses that
            can't afford downtime.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#services"
              data-testid="hero-cta-services"
              className="group inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#FF8533] text-white px-6 py-3.5 font-semibold tracking-wide transition-all shadow-[0_0_15px_rgba(255,107,0,0.3)] hover:shadow-[0_0_30px_rgba(255,107,0,0.5)]"
            >
              Explore Services
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF6B00] hover:text-[#FF6B00] text-white px-6 py-3.5 font-semibold tracking-wide transition-all"
            >
              Get a Free Site Audit
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-10 flex flex-wrap gap-2.5"
            data-testid="hero-service-pills"
          >
            {PILLS.map(({ Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-[#A0B2C6] border border-white/8 bg-[#0B1320]/60"
              >
                <Icon size={13} className="text-[#FF6B00]" strokeWidth={1.8} />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 bg-[#0B1320]">
            <img
              src="https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Glowing fiber optics"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#060B14] via-[#060B14]/40 to-transparent" />

            {/* Health score badge */}
            <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#060B14]/80 backdrop-blur-md border border-white/10 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300">live</span>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6] text-right">
                health<br/>score
              </div>
            </div>

            <div className="absolute left-5 right-5 bottom-5">
              <div className="bg-[#060B14]/85 backdrop-blur-xl border border-white/10 p-5">
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6]">network · uplink</div>
                    <div className="font-display text-5xl font-bold text-white mt-1">98<span className="text-2xl text-[#FF6B00]">%</span></div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6]">latency</div>
                    <div className="font-display text-xl font-semibold mt-1">12<span className="text-xs text-[#A0B2C6] ml-1">ms</span></div>
                  </div>
                </div>
                <div className="h-1.5 bg-white/10 rounded overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-[#FF6B00]" style={{ width: "98%" }} />
                </div>
              </div>
            </div>
          </div>

          {/* Floating chip */}
          <div className="absolute -left-6 top-1/3 hidden md:block">
            <div className="bg-[#0B1320] border border-white/10 px-4 py-3 shadow-2xl">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6]">Wi-Fi heatmap</div>
              <div className="flex items-center gap-1.5 mt-2">
                <span className="heat-blob bg-emerald-400 w-3 h-3 static" />
                <span className="heat-blob bg-yellow-400 w-3 h-3 static" />
                <span className="heat-blob bg-[#FF6B00] w-3 h-3 static" />
                <span className="heat-blob bg-red-500 w-3 h-3 static" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats strip */}
      <div className="relative max-w-7xl mx-auto mt-20 px-5 sm:px-8">
        <div className="grid grid-cols-3 gap-4 sm:gap-10 border-y border-white/8 py-7" data-testid="hero-stats">
          {STATS.map((s) => (
            <div key={s.label} className="flex flex-col gap-1 items-start">
              <span className="font-display text-3xl sm:text-4xl font-bold text-white">{s.value}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6]">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
